import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { ProjectProvider } from '@battleops/client/src/context/ProjectContext';
import { DepartmentProvider } from '@battleops/client/src/context/DepartmentContext';
import { TaskProvider } from '@battleops/client/src/context/TaskContext';
import { TimeBlockProvider } from '@battleops/client/src/context/TimeBlockContext';
import { NoteProvider } from '@battleops/client/src/context/NoteContext';
import { MilestoneProvider } from '@battleops/client/src/context/MilestoneContext';
import { ProcessProvider } from '@battleops/client/src/context/ProcessContext';
import { SectionProvider } from '@battleops/client/src/context/SectionContext';
import { DepartmentFollowsPlan } from '@battleops/client/src/data/followPlan';
import { CardActionProvider } from '@battleops/client/src/context/CardActionContext';
import Shell from './shell/Shell';
import Home from './pages/Home';

// Pages load on first visit (the notes editor and charts stay out of the first paint).
const page = (load) => { const C = lazy(load); return <Suspense fallback={<div className="page faint">Loading…</div>}><C /></Suspense>; };

const router = createBrowserRouter([
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Home /> },
      { path: 'projects', element: page(() => import('./pages/Projects')) },
      { path: 'projects/:projectId', element: page(() => import('./pages/Project')) },
      { path: 'projects/:projectId/:tab', element: page(() => import('./pages/Project')) },
      { path: 'plan', element: page(() => import('./pages/planner/Planner')) },
      { path: 'plan/:date', element: page(() => import('./pages/planner/Planner')) },
      { path: 'week', element: page(() => import('./pages/Week')) },
      { path: 'week/:key', element: page(() => import('./pages/Week')) },
      { path: 'calendar', element: page(() => import('./pages/Calendar')) },
      { path: 'forecast', element: page(() => import('./pages/Forecast')) },
      { path: 'calendar/:view', element: page(() => import('./pages/Calendar')) },
      { path: 'calendar/:view/:date', element: page(() => import('./pages/Calendar')) },
      { path: 'time', element: page(() => import('./pages/Time')) },
      { path: 'time/:tab', element: page(() => import('./pages/Time')) },
      { path: 'insights', element: page(() => import('./pages/insights/Insights')) },
      { path: 'insights/:tab', element: page(() => import('./pages/insights/Insights')) },
      { path: 'notes', element: page(() => import('./pages/notes/Notes')) },
      { path: 'notes/:scope', element: page(() => import('./pages/notes/Notes')) },
      { path: 'notes/:scope/:noteId', element: page(() => import('./pages/notes/Notes')) },
      { path: 'log', element: page(() => import('./pages/DailyLog')) },
      { path: 'log/:date', element: page(() => import('./pages/DailyLog')) },
      { path: 'track', element: page(() => import('./pages/Track')) },
      { path: 'track/:id', element: page(() => import('./pages/Track')) },
      { path: 'cfo', element: page(() => import('./pages/Cfo')) },
      { path: 'department/:id', element: page(() => import('./pages/Department')) },
      { path: 'board', element: page(() => import('./pages/Board')) },   // boards (docs/plans/boards.md B3)
      { path: 'board/:kind/:id', element: page(() => import('./pages/Board')) },
      { path: 'tag/:slug', element: page(() => import('./pages/Tag')) },
      { path: 'photos', element: page(() => import('./pages/Photos')) },   // every job's photos (v0.1.265)
      { path: 'trips', element: page(() => import('./pages/Trips')) },
      { path: 'trips/:date', element: page(() => import('./pages/Trips')) },
      { path: 'reminders', element: page(() => import('./pages/Reminders')) },
      { path: 'approvals', element: page(() => import('./pages/Approvals')) },
      { path: 'team', element: page(() => import('./pages/Team')) },
      { path: 'team/:uid', element: page(() => import('./pages/Profile')) },
      { path: 'profile', element: page(() => import('./pages/Profile')) },
      { path: 'settings', element: page(() => import('./pages/Setup')) },
      { path: 'settings/:tab', element: page(() => import('./pages/Setup')) },
      { path: 'settings/:tab/:sub', element: page(() => import('./pages/Setup')) },
      { path: 'routines', element: page(() => import('./pages/routines/Routines')) },
      { path: 'routines/:id', element: page(() => import('./pages/routines/Routines')) },
      { path: 'routines/:id/:sub', element: page(() => import('./pages/routines/Routines')) },
      { path: 'processes', element: <Navigate to="/routines" replace /> },
      { path: 'screen-time', element: <Navigate to="/insights/phone" replace /> },
      { path: '*', element: page(() => import('./pages/Placeholder')) },
    ],
  },
]);

// The same providers as the phone (App.js), minus location (phone-only).
export default function AppRoutes() {
  return (
    <ProjectProvider>
      <DepartmentProvider>
        <TaskProvider>
          <TimeBlockProvider>
            <NoteProvider>
              <MilestoneProvider>
                <ProcessProvider>
                  <SectionProvider>
                    <CardActionProvider>
                      <DepartmentFollowsPlan />
                      <RouterProvider router={router} />
                    </CardActionProvider>
                  </SectionProvider>
                </ProcessProvider>
              </MilestoneProvider>
            </NoteProvider>
          </TimeBlockProvider>
        </TaskProvider>
      </DepartmentProvider>
    </ProjectProvider>
  );
}
